import React, { useEffect, useMemo } from 'react';
import { bindActionCreators } from 'redux';
import { connect, useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import dayjs from 'dayjs';

// Components

import Form from '@lib/wrappers/Form';
import UniversalInput from '@lib/wrappers/UniversalInput';
import InstructionalText from '../notifications/InstructionalText';
import { ConditionalRendering } from '@components/common/conditional-rendering';
import ValidationErrorMessage from '@components/ssap/notifications/ValidationErrorMessage';

// ActionCreators / selectors

import { changeData, fetchEnrollments } from '@actions/ssap/ssapActionCreators';
import {
    selectCurrentlyPregnantAndEnrolled,
    selectEndDateOfPregnancyOverride,
    useExchangeAndProgramNames,
    selectFormSnapshot,
    selectIfShouldFetchEnrollments,
    selectIsDataFetched,
} from '@reducers/appReducer';

// Utils

import { cloneObject } from '@utils/miscellaneous/cloneObject';
import { dataSyncingTools } from '@utils/tools/dataSyncingTools';
import { getValueFromSsapFormSnapshot } from '@utils/miscellaneous/getValueFromSsapFormSnapshot';
import { getWomanOfReproductiveAge } from '@utils/householdMember/getWomanOfReproductiveAge';
import { preventerTools } from '@utils/tools/preventerTools';
import { normalizeNameOfPerson } from '@utils/normalize/normalizeNameOfPerson';
import { parseFieldChangeArgs } from '@utils/ssap/parseFieldChangeArgs';
import { TIME_INTERVALS } from '@utils/dates/dateMethods';

// Constants

import { DROPDOWN_OPTIONS } from '@constants/ssap/hhmTypes/dropdownOptions';
import { EMPTY_OBJECTS } from '@constants/ssap/initialValues/emptyObjects';
import { GENDER_TYPES } from '@constants/ssap/miscellaneous/miscellaneous';
import { INPUT_TYPES } from '@constants/inputs/inputTypes';
import { VALIDATION_TYPES } from '@constants/inputs/validationTypes';
import { VALIDATION_ERROR_MESSAGES } from '@constants/inputs/validationErrorMessages';

/**
 *
 * @param {Object} props
 * @returns {React.Component} - 'Additional Information - Member Preganant Details ' Page
 *
 */
const Page2_22 = props => {

    const {
        data,
        data: {
            taxHousehold: {
                thCommonParams: {
                    hasSomeonePregnant
                }
            }
        },
        environment: {
            exchangeName,
            isState,
            localDates: {
                serverDate
            },
            stateCode
        },
        householdMembers,
        form,
        formName,
        page
    } = props;

    const {
        isFormSubmitted
    } = preventerTools.provideObject(form[formName]);

    const { exchangeChip, exchangeMedicaid, exchangeMedicaidOrChip, exchangeMedicaidSlashChip } = useExchangeAndProgramNames();
    const currentlyPregnantAndEnrolled = useSelector(selectCurrentlyPregnantAndEnrolled);
    const shouldFetchEnrollments = useSelector(selectIfShouldFetchEnrollments);
    const isEnrollmentsDataFetched = useSelector(selectIsDataFetched);
    const snapshot = useSelector(state => selectFormSnapshot(state, formName));
    const endDateOfPregnancyOverride = useSelector(selectEndDateOfPregnancyOverride);
    const pregnancyLookbackDays = endDateOfPregnancyOverride === TIME_INTERVALS.ONE_YEAR ? 365 : endDateOfPregnancyOverride ?? 60;
    const pregnancyLookbackMessageValues = { days: pregnancyLookbackDays, months: Math.round(pregnancyLookbackDays / 30) };

    useEffect(() => {
        shouldFetchEnrollments && props.actions.fetchEnrollments();
    }, []);

    const onFieldChange_member = (...args) => {

        const { indexInArray, value } = parseFieldChangeArgs(args);

        const newState = { ...data };

        if(!value) {
            newState.taxHousehold.householdMember[indexInArray].specialCircumstances = {
                ...newState.taxHousehold.householdMember[indexInArray].specialCircumstances,
                pregnantIndicator: false,
                dueDateOfPregnancy: null,
                numberBabiesExpectedInPregnancy: 0,
                currentlyPregnant: null,
                endDateOfPregnancy: null,
                startDateOfPregnancy: null,
            };

            newState.taxHousehold.householdMember[indexInArray].evaluateForMedicaidIndicator = true;
        }
        newState.taxHousehold.householdMember[indexInArray].specialCircumstances.pregnantIndicator = value;

        const syncedData = dataSyncingTools.syncThCommonProps(
            newState,
            { propName: 'pregnant' }
        );

        props.actions.changeData(syncedData);
    };

    const onFieldChange_isStillPregnant = (...args) => {
        let { indexInArray, newData, value } = parseFieldChangeArgs(args);
        const newState = { ...data };

        newData = {
            ...newData,
            specialCircumstances: {
                ...newData.specialCircumstances,
                ...(
                    value
                        ? { endDateOfPregnancy: null }
                        : { dueDateOfPregnancy: null, numberBabiesExpectedInPregnancy: 0 }
                )
            }
        };

        if(!value) {
            newData.evaluateForMedicaidIndicator = true;
        }

        newState.taxHousehold.householdMember[indexInArray] = newData;
        props.actions.changeData(newState);
    };

    const onFieldChange_value = (...args) => {
        const { fields, indexInArray, name, newData, value } = parseFieldChangeArgs(args);
        const newState = cloneObject(data);

        newState.taxHousehold.householdMember[indexInArray] = newData;

        if (name === 'dueDateOfPregnancy') {
            const wasPregnant = getValueFromSsapFormSnapshot(snapshot, page, indexInArray, fields,'pregnantOnPreviousApplication');
            const initDueDate = getValueFromSsapFormSnapshot(snapshot, page, indexInArray, fields, 'dueDateOfPregnancy');

            const newDueDate = value;

            // Dates sequence
            const isCurrentDateAfterInitDueDate = dayjs(serverDate).isAfter(initDueDate);
            const isCurrentDateBeforeInitDueDate = dayjs(serverDate).isBefore(initDueDate);
            const isNewDueDateAfterInitDueDate = dayjs(newDueDate).isAfter(initDueDate);
            const isNewDueDateAfterCurrentDate = dayjs(newDueDate).isAfter(serverDate);
            const isNewDueDateBeforeCurrentDate = dayjs(newDueDate).isBefore(serverDate);

            if(wasPregnant) {
                if(isCurrentDateAfterInitDueDate) {
                    if(isNewDueDateAfterInitDueDate && isNewDueDateAfterCurrentDate) {
                        // case 1: application is edited after Due Date, we recognize it as a new pregnancy, new Due Date moved to the future
                        newState
                            .taxHousehold
                            .householdMember[indexInArray]
                            .specialCircumstances
                            .pregnantOnPreviousApplication = null;
                    } else if(isNewDueDateBeforeCurrentDate) {
                        // case 2: application is edited after Due Date, handling if User roll back Due Date
                        newState
                            .taxHousehold
                            .householdMember[indexInArray]
                            .specialCircumstances
                            .pregnantOnPreviousApplication = true;
                    }
                }

                // case 3: application is edited before Due Date, we recognize that User adjusts Due Date, we should keep radio buttons disabled
                if(isCurrentDateBeforeInitDueDate) {
                    if(isNewDueDateAfterInitDueDate && isNewDueDateAfterCurrentDate) {
                        newState
                            .taxHousehold
                            .householdMember[indexInArray]
                            .specialCircumstances
                            .pregnantOnPreviousApplication = true;
                    } else if(isNewDueDateBeforeCurrentDate) {
                        newState
                            .taxHousehold
                            .householdMember[indexInArray]
                            .specialCircumstances
                            .pregnantOnPreviousApplication = true;
                    }
                }
            }
        }

        props.actions.changeData(newState);
    };

    const onFieldChange_none_of_above = (...args) => {

        const [ newData ] = args;

        const updateIndividualValues = hhm => {
            return {
                ...hhm,
                specialCircumstances: {
                    ...hhm.specialCircumstances,
                    ...cloneObject(EMPTY_OBJECTS.SPECIAL_CIRCUMSTANCES)
                }
            };
        };

        const syncedData = dataSyncingTools.syncThCommonProps(
            newData,
            {
                customUpdater: updateIndividualValues,
                isCommon: true,
                propName: 'pregnant'
            }
        );

        props.actions.changeData(syncedData);
    };

    const getRangeOfDays = () => {
        switch(true) {
            case isState.VA:
            case isState.GI:
                return TIME_INTERVALS.ONE_YEAR;
            case isState.IL:
            case isState.OR:
            case isState.MN:
            case isState.PA:
                return TIME_INTERVALS.NINETY_DAYS;
            default:
                return TIME_INTERVALS.SIXTY_DAYS;
        }
    };

    const getHouseholdMembers = (householdMembers) => {
        const membersVisible = householdMembers
            .map((member, i) => ({
                member,
                originalIndex: i,
                isVisible: !!getWomanOfReproductiveAge(member, serverDate)
            }))
            .filter(({ isVisible }) => isVisible);

        const combinedMembers = membersVisible.map(({ member, originalIndex: i }) => {
            const {
                applicantGuid,
                evaluateForMedicaidIndicator,
                gender,
                name,
                specialCircumstances: {
                    currentlyPregnant,
                    dueDateOfPregnancy,
                    endDateOfPregnancy,
                    numberBabiesExpectedInPregnancy,
                    pregnantIndicator,
                    pregnantOnPreviousApplication,
                    startDateOfPregnancy
                }
            } = member;

            const personName = normalizeNameOfPerson(name);

            // Disabling/enabling radio buttons
            const initDueDate = getValueFromSsapFormSnapshot(snapshot, page, i, [ 'specialCircumstances' ], 'dueDateOfPregnancy');
            const stillPregnantBecauseOfDueDate = dayjs(serverDate).isBefore(initDueDate);
            const isFollowUpQuestionDisabled = pregnantOnPreviousApplication === true && stillPregnantBecauseOfDueDate;

            const isMedicaidEvaluationQuestionShowed = currentlyPregnantAndEnrolled.includes(applicantGuid);

            return (
                <li key = { applicantGuid }>
                    <UniversalInput
                        label = { {
                            text: 'common.input.label.non_internationalized_text',
                            labelTextReplacements: {
                                text: personName
                            }
                        } }
                        inputData = { {
                            isInputRequired: false,
                            type: INPUT_TYPES.CHECKBOX,
                            name: 'pregnantIndicator',
                            classes: [''],
                            checked: pregnantIndicator,
                            value: pregnantIndicator,
                            currentData: member,
                            fields: [],
                        } }
                        inputActions = { {
                            onFieldChange: onFieldChange_member
                        } }
                        indexInArray = { i }
                        errorClasses = { [ 'margin-l-0' ] }
                    />

                    <ConditionalRendering isTrue={pregnantIndicator && gender === GENDER_TYPES.FEMALE}>
                        <ConditionalRendering isTrue={isState.IL}>
                            <UniversalInput
                                label={{
                                    labelClass: ['padding-0'],
                                    text: 'ssap.input.label.pregnancy_confirmation_date',
                                    dataInstructions: 'instructional_text.pregnancy_confirmation_date',
                                    onlyInstructions: true,
                                    replacements: {
                                        exchangeMedicaid
                                    }
                                }}
                                inputData={{
                                    isInputRequired: false,
                                    isInputStandalone: true,
                                    type: INPUT_TYPES.DATE_PICKER,
                                    name: 'startDateOfPregnancy',
                                    value: startDateOfPregnancy,
                                    valuesToMatch: serverDate,
                                    placeholder: 'MM/DD/YYYY',
                                    validationType: VALIDATION_TYPES.DATE_NOT_GREATER_THAN_CURRENT,
                                    currentData: member,
                                    fields: ['specialCircumstances']
                                }}
                                inputActions={{
                                    onFieldChange: onFieldChange_value
                                }}
                                errorClasses={['margin-l-0']}
                                indexInArray={i}
                            />
                        </ConditionalRendering>
                        <UniversalInput
                            legend = { {
                                legendText: 'ssap.input.legend.is_still_pregnant',
                                legendTextReplacements: { personName }
                            } }
                            label = { {
                                showRequiredIcon: true
                            } }
                            inputData = { {
                                isInputRequired: true,
                                type: INPUT_TYPES.RADIO_BUTTONS,
                                name: 'currentlyPregnant',
                                value: currentlyPregnant,
                                options: DROPDOWN_OPTIONS.YES_NO,
                                validationType: VALIDATION_TYPES.CHOSEN,
                                validationErrorMessage: VALIDATION_ERROR_MESSAGES.MAKE_SELECTION,
                                currentData: member,
                                fields: [ 'specialCircumstances' ]
                            } }
                            inputActions = { {
                                onFieldChange: onFieldChange_isStillPregnant
                            } }
                            errorClasses = { ['margin-l-0'] }
                            indexInArray = { i }
                        />

                        <ConditionalRendering isTrue={currentlyPregnant}>
                            <p className = 'required-true' id="numberBabiesExpectedInPregnancy" data-testid='numberBabiesExpectedInPregnancy_legend'>
                                <FormattedMessage id = 'ssap.family_household.pregnancy.number_of_babies' />
                            </p>

                            <UniversalInput
                                label = { {
                                    labelClasses: [ 'required-false' ]
                                } }
                                button = { {
                                    btnLId: 'decrement',
                                    btnRId: 'increment',
                                    rBtnText: '+',
                                    lBtnText: '-',
                                    minCounter: 1,
                                    maxCounter: 19,
                                    btnClasses: [ 'countBtn' ]
                                } }
                                inputData = { {
                                    isInputRequired: true,
                                    type: INPUT_TYPES.INPUT_LR_BUTTONS,
                                    name: 'numberBabiesExpectedInPregnancy',
                                    outerClass: [ 'grid-row' ],
                                    value: !Number(numberBabiesExpectedInPregnancy)
                                        ? 1
                                        : Number(numberBabiesExpectedInPregnancy),
                                    placeholder: '',
                                    validationType: VALIDATION_TYPES.ALL_DIGITS,
                                    currentData: member,
                                    fields: [ 'specialCircumstances' ]
                                } }
                                inputActions = { {
                                    onFieldChange: onFieldChange_value
                                } }
                                indexInArray = { i }
                            />

                            <UniversalInput
                                label = { {
                                    labelClass: [ 'padding-0' ],
                                    text: 'ssap.input.label.pregnancy_due_date'
                                } }
                                inputData = { {
                                    isInputRequired: true,
                                    type: INPUT_TYPES.DATE_PICKER,
                                    name: 'dueDateOfPregnancy',
                                    value: dueDateOfPregnancy,
                                    valuesToMatch: [ serverDate, isState ],
                                    placeholder: 'MM/DD/YYY',
                                    validationType: VALIDATION_TYPES.DATE_PREGNANCY,
                                    currentData: member,
                                    fields: [ 'specialCircumstances' ]
                                } }
                                inputActions = { {
                                    onFieldChange: onFieldChange_value
                                } }
                                errorClasses = { [ 'margin-l-0' ] }
                                indexInArray = { i }
                            />
                        </ConditionalRendering>

                        <ConditionalRendering isTrue={currentlyPregnant === false}>
                            <UniversalInput
                                label = { {
                                    labelClass: [ 'padding-0' ],
                                    text: 'ssap.input.label.pregnancy_end_date',
                                    dataInstructions: 'instructional_text.pregnancy_end_date',
                                    dataInstructionsAnchor: isState.NV
                                        ? 'instructional_text.learn_more_anchor.mk_2'
                                        : null,
                                    replacements: {
                                        exchangeMedicaid
                                    }
                                } }
                                inputData = { {
                                    isInputRequired: false,
                                    type: INPUT_TYPES.DATE_PICKER,
                                    name: 'endDateOfPregnancy',
                                    value: endDateOfPregnancy,
                                    valuesToMatch: [
                                        serverDate,
                                        endDateOfPregnancyOverride
                                    ],
                                    specificValidation: {
                                        validationType: VALIDATION_TYPES.LAST_N_DAYS
                                    },
                                    validationErrorMessage: VALIDATION_ERROR_MESSAGES.PREGNANCY_END_DATE,
                                    validationErrorValues: pregnancyLookbackMessageValues,
                                    placeholder: 'MM/DD/YYY',
                                    currentData: member,
                                    fields: [ 'specialCircumstances' ]
                                } }
                                inputActions = { {
                                    onFieldChange: onFieldChange_value
                                } }
                                errorClasses = { [ 'margin-l-0' ] }
                                indexInArray = { i }
                            />
                        </ConditionalRendering>

                        <ConditionalRendering isTrue={isMedicaidEvaluationQuestionShowed}>
                            <p>
                                <FormattedMessage
                                    id = 'ssap.medicaid_qualification_notice'
                                    values={{ exchangeMedicaid, exchangeChip, exchangeMedicaidSlashChip }}
                                />
                                <ConditionalRendering isTrue = { isState.VA } >
                                    <InstructionalText
                                        Component = {
                                            <span data-instructions = 'instructional_text.evaluate_for_medicaid' />
                                        }
                                        replacements = { {
                                            exchangeChip,
                                            exchangeMedicaid
                                        } }
                                    />
                                </ConditionalRendering>
                            </p>
                            <UniversalInput
                                legend = { {
                                    legendText: 'ssap.input.legend.tax_credit_preference',
                                    legendTextReplacements: {
                                        personName,
                                        exchangeChip,
                                        exchangeMedicaid,
                                        exchangeMedicaidOrChip,
                                        exchangeMedicaidSlashChip
                                    }
                                } }
                                label = { {
                                    showRequiredIcon: true
                                } }
                                inputData = { {
                                    isInputRequired: true,
                                    disabled: isFollowUpQuestionDisabled,
                                    type: INPUT_TYPES.RADIO_BUTTONS,
                                    name: 'evaluateForMedicaidIndicator',
                                    value: evaluateForMedicaidIndicator,
                                    options: DROPDOWN_OPTIONS.PREFERENCES_FOR_PREGNANT(stateCode),
                                    optionsReplacements: {
                                        exchangeChip,
                                        exchangeMedicaid,
                                        exchangeMedicaidOrChip,
                                        exchangeMedicaidSlashChip,
                                        exchangeName,
                                        personName
                                    },
                                    validationType: VALIDATION_TYPES.CHOSEN,
                                    validationErrorMessage: VALIDATION_ERROR_MESSAGES.MAKE_SELECTION,
                                    currentData: member,
                                    fields: [ ]
                                } }
                                inputActions = { {
                                    onFieldChange: onFieldChange_value
                                } }
                                errorClasses = { ['margin-l-0'] }
                                indexInArray = { i }
                            />

                            <UniversalInput
                                inputData = { {
                                    isInputRequired: false,
                                    type: INPUT_TYPES.DUMMY,
                                    name: 'pregnantOnPreviousApplication',
                                    value: pregnantOnPreviousApplication,
                                    fields: [ 'specialCircumstances' ],
                                    isInputStandalone: true
                                } }
                                indexInArray = { i }
                            />
                        </ConditionalRendering>
                    </ConditionalRendering>
                </li>
            );
        });

        return (
            <ul className = 'usa-list usa-list--unstyled' >
                {combinedMembers}

                <li key = 'hasSomeonePregnant' >
                    <UniversalInput
                        label = { {
                            text: 'ssap.input.label.none_of_above',
                            isLabelRequired: false
                        } }
                        inputData = { {
                            isInputRequired: false,
                            type: INPUT_TYPES.CHECKBOX,
                            name: 'hasSomeonePregnant',
                            checked: hasSomeonePregnant === false,
                            value: hasSomeonePregnant === false,
                            currentData: cloneObject(data),
                            fields: [ 'taxHousehold', 'thCommonParams' ],
                            reversedValue: true
                        } }
                        inputActions = { {
                            onFieldChange: onFieldChange_none_of_above
                        } }
                    />
                </li>
            </ul>
        );
    };

    const topLevelValidity = useMemo(
        () => {
            return householdMembers.some(hhm => hhm.specialCircumstances.pregnantIndicator) || hasSomeonePregnant === false;
        },
        [ householdMembers, hasSomeonePregnant ]
    );

    if(!isEnrollmentsDataFetched) {
        return null;
    }

    return (
        <div
            className = 'fade-in'
            id = 'fhMoreInfo'
        >
            <Form
                { ...{
                    formName,
                    topLevelValidity
                } }
            >
                <div className = 'subsection' >
                    <ValidationErrorMessage
                        isVisible = { isFormSubmitted && !topLevelValidity }
                        messageText = { VALIDATION_ERROR_MESSAGES.ANY_VALUE }
                        errorClasses = { [ 'margin-l-0' ] }
                    />
                    <fieldset>
                        <legend>
                            <p className = 'required-true' data-testid = 'pregnancy_details_legend' >
                                <FormattedMessage
                                    id='ssap.family_household.pregnancy.any_pregnant'
                                    values={pregnancyLookbackMessageValues}
                                />
                            </p>
                        </legend>
                        { getHouseholdMembers(householdMembers) }
                    </fieldset>
                </div>
            </Form>
        </div>
    );
};

const mapStateToProps = state => ({
    environment: state.common.environment,
    
    data: state.ssap.data,
    form: state.ssap.form,

    householdMembers: state
        .ssap
        .data
        .taxHousehold
        .householdMember,

    page: state.ssap.page
});

const mapDispatchToProps = dispatch => ({
    actions: bindActionCreators({
        changeData,
        fetchEnrollments
    }, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(Page2_22);
